import type { TranslationCatalog, TranslationKey } from "./types.js";
import { DEFAULT_LANGUAGE, isLanguageCode, type LanguageCode } from "./languages.js";
import srLatn from "./locales/sr-Latn.js";
import srCyrl from "./locales/sr-Cyrl.js";
import en from "./locales/en.js";
import bs from "./locales/bs.js";
import hr from "./locales/hr.js";
import mk from "./locales/mk.js";
import hu from "./locales/hu.js";
import sk from "./locales/sk.js";
import sl from "./locales/sl.js";
import ro from "./locales/ro.js";
import rue from "./locales/rue.js";

const CATALOGS: Record<LanguageCode, TranslationCatalog> = {
  "sr-Latn": srLatn,
  "sr-Cyrl": srCyrl,
  en,
  bs,
  hr,
  mk,
  hu,
  sk,
  sl,
  ro,
  rue,
};

// Same shape as apps/web/src/audio/sfx.ts's isMuted()/setMuted() — a single
// localStorage-backed module value, not routed through the Zustand store
// itself (see appStore.ts's `language` field, which mirrors this on every
// change so main.ts's render() subscription re-renders with the new t()
// output — this module has no framework dependency of its own).
const LANGUAGE_KEY = "meksiko.language";

function readInitialLanguage(): LanguageCode {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    if (stored && isLanguageCode(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, etc.) — fall through to default
  }
  return DEFAULT_LANGUAGE;
}

let currentLanguage: LanguageCode = readInitialLanguage();

export function getLanguage(): LanguageCode {
  return currentLanguage;
}

export function setLanguage(lang: LanguageCode): void {
  currentLanguage = lang;
  try {
    localStorage.setItem(LANGUAGE_KEY, lang);
  } catch {
    // Preference just won't persist across reloads — not worth surfacing.
  }
}

/**
 * Reads the given key from the currently active locale's catalog. A locale
 * missing at runtime (should be impossible — `satisfies TranslationCatalog`
 * guarantees every locale file has every key) falls back to sr-Latn's value
 * for that key rather than crashing.
 */
export function t<K extends TranslationKey>(
  key: K,
  ...args: TranslationCatalog[K] extends (params: infer P) => string ? [params: P] : []
): string {
  const catalog = CATALOGS[currentLanguage] ?? srLatn;
  const entry = (catalog[key] ?? srLatn[key]) as TranslationCatalog[K];
  if (typeof entry === "function") {
    return (entry as (p: unknown) => string)(args[0]);
  }
  return entry as string;
}

/**
 * Looks up a GameEngineError `code` (or a join-rejection / matchStartFailed
 * reason, which are already bare code-like strings on the wire — see
 * MeksikoRoom.ts) under the `errors.*` namespace, falling back to a generic
 * message for any code this client's catalog doesn't recognize (e.g. an
 * older client talking to a newer server build).
 */
export function tError(code: string): string {
  const key = `errors.${code}` as TranslationKey;
  return key in srLatn ? t(key) : t("common.unknownError");
}

export { LANGUAGES, isLanguageCode, DEFAULT_LANGUAGE, type LanguageCode } from "./languages.js";
